import { memo, useMemo, useState } from "react";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useWeekViewContext } from "@/contexts/WeekView";
import { isShiftDayInPast } from "@/utils/dateUtils";
import type { EmploymentType, NurseFullRole } from "@/utils/models";
import { StaffingChanges } from "../StaffingChanges/StaffingChanges";
import { DayBlock } from "./DayBlock";
import Style from "./StaffingBlock.module.scss";

type Props = {
	shiftType: string;
	rolesToDisplay: NurseFullRole[];
	employmentTypesToDisplay: EmploymentType[];
	selectedJobIds?: string[];
	isFullDay: boolean;
	highlightedNurseId?: number;
	/** The Annie walkthrough anchor goes on one day of one block; the page renders a block per shift type. */
	isFirstShiftBlock?: boolean;
};

export const StaffingBlock = memo(
	({ shiftType, rolesToDisplay, employmentTypesToDisplay, selectedJobIds, isFullDay, highlightedNurseId, isFirstShiftBlock }: Props) => {
		const { timezone } = useHospitalData();
		const { scheduleWeeks, scheduleWeekIndex } = useWeekViewContext();
		// DEFAULT: a week still loading has no rows yet and draws an empty block.
		const days = scheduleWeeks[scheduleWeekIndex]?.weeklyShifts.find((shift) => shift.type === shiftType)?.days ?? [];
		const [selectedDate, setSelectedDate] = useState(days.at(0)?.date);
		const [isAdd, setIsAdd] = useState(false);
		const [suggestionsRendering, setSuggestionsRendering] = useState(false);

		const handleAddClick = (dayIndex: number) => {
			setSelectedDate(days.at(dayIndex)?.date);
			setIsAdd(true);
			setSuggestionsRendering(true);
		};

		const handleDialogClose = () => {
			setIsAdd(false);
			setSuggestionsRendering(false);
		};

		// The day the Annie "post an open shift" walkthrough spotlights, in the first shift block only.
		// The same predicate `WeekSchedule` and the Post Shift tab use, so the two grids cannot disagree
		// about which day a department switching layouts gets.
		const postShiftAnchorDayIndex = useMemo<number>(() => {
			// An unloaded week anchors nothing, and `findIndex` on an empty list would say -1 anyway.
			if (!isFirstShiftBlock || !days.length) return -1;
			return days.findIndex((day) => !isShiftDayInPast(day.date, timezone));
		}, [isFirstShiftBlock, days, timezone]);

		return (
			<>
				<div className={Style.overlayCon}>
					{days.map((day, dayIndex) => (
						<DayBlock
							key={day.date.toISODate()}
							date={day.date}
							shiftType={shiftType}
							rolesToDisplay={rolesToDisplay}
							employmentTypesToDisplay={employmentTypesToDisplay}
							selectedJobIds={selectedJobIds}
							isFullDay={isFullDay}
							onAddClick={handleAddClick}
							highlightedNurseId={highlightedNurseId}
							isPostShiftAnchor={dayIndex === postShiftAnchorDayIndex}
						/>
					))}
				</div>
				{isAdd && selectedDate != null && (
					<StaffingChanges setIsAdd={handleDialogClose} date={selectedDate} shiftType={shiftType} rolesToDisplay={rolesToDisplay} onClose={handleDialogClose} />
				)}
			</>
		);
	},
);
